import React, { useMemo } from 'react';
import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useThemeVersion } from '../../../useTheme';
import { MemoChart } from './MemoChart';

// Lê as cores do tema ativo (não hardcoded) — precisa ser recalculada
// quando o usuário troca de tema (ver useThemeVersion), senão o gráfico
// fica com a cor antiga presa e o texto some no fundo do tema novo.
const buildDefaultChartOptions = () => {
    const style = getComputedStyle(document.documentElement);
    const textColor = style.getPropertyValue('--text-color') || '#495057';
    const gridColor = style.getPropertyValue('--surface-border') || '#ebedef';
    return {
        plugins: {
            legend: {
                labels: {
                    color: textColor
                }
            }
        },
        scales: {
            y: {
                ticks: {
                    color: textColor
                },
                grid: {
                    color: gridColor
                }
            },
            x: {
                ticks: {
                    color: textColor
                },
                grid: {
                    color: gridColor
                }
            }
        }
    };
};

export const ChartCard = ({ title, loading, error, onReload, headerExtra, children }) => {
    // Assume um único filho (Chart/MemoChart) — é como todo uso atual chama
    // ChartCard. Injeta as opções padrão, preservando o que o filho já
    // definir. Memoizado pela referência de child.props.options: sem isso,
    // o spread recriaria um objeto novo a cada render do ChartCard (mesmo
    // com conteúdo idêntico), invalidando qualquer React.memo no filho e
    // recriando/reanimando o gráfico à toa.
    //
    // Só injeta `options` se o filho for de fato um <Chart> do PrimeReact
    // (ou o MemoChart, que envolve um) — outros filhos (ex.: o
    // <ResponsiveContainer> do recharts, usado no treemap de Serviços
    // Médicos) não têm essa prop e o ResponsiveContainer espalha qualquer
    // prop desconhecida no <div> real, gerando um atributo DOM inválido.
    const themeVersion = useThemeVersion();
    const child = React.Children.only(children);
    const isPrimeChart = React.isValidElement(child) && (child.type === Chart || child.type === MemoChart);
    const childOptions = isPrimeChart ? child.props.options : undefined;
    const mergedOptions = useMemo(
        () => ({ ...buildDefaultChartOptions(), ...childOptions }),
        // themeVersion não é lido aqui dentro, mas precisa disparar essa
        // memo de novo (buildDefaultChartOptions relê getComputedStyle).
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [childOptions, themeVersion]
    );
    const childWithProps = isPrimeChart
        ? React.cloneElement(child, { options: mergedOptions })
        : child;

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-3">
                <h5 className="m-0">{title}</h5>
                <div className="flex align-items-center gap-1">
                    {headerExtra}
                    {onReload && (
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={onReload}
                            disabled={loading}
                        />
                    )}
                </div>
            </div>
            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ height: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : error ? (
                <div className="flex flex-column align-items-center justify-content-center p-5">
                    <i className="pi pi-exclamation-triangle mb-3" style={{ fontSize: '2rem', color: 'var(--red-500)' }}></i>
                    <span className="text-lg">{error}</span>
                    {onReload && <Button label="Tentar Novamente" className="p-button-text mt-3" onClick={onReload} />}
                </div>
            ) : (
                childWithProps
            )}
        </div>
    );
}; 